import { Injectable, signal } from '@angular/core';
import type { OrgUnitSettingsSectionId } from './org-unit-settings-sections';

/**
 * The configuration section last looked at.
 *
 * Held outside the panel because the panel is rebuilt for each unit, and the
 * point is to survive exactly that: clicking the gear on one unit after
 * another should not mean re-opening the same section each time. What is being
 * maintained is a subject — "I am going through everyone's time types" — so it
 * is deliberately *not* keyed by unit.
 *
 * Closing the preview ends that, though. `reset()` puts the breadcrumb back to
 * the top, because a sheet reopened later is a new piece of work; carrying the
 * old trail into it reads as the panel having a mind of its own.
 *
 * The panel ignores a remembered section a unit does not offer, so nothing here
 * can strand someone on a section they may not see.
 */
/**
 * An entry a section is currently showing the form for.
 *
 * `close` is supplied by the section itself: the breadcrumb has to be able to
 * send it back to its list, and the panel cannot reach a component that reaches
 * it through content projection.
 */
export interface OpenSettingsItem {
  /** The entry's own name, shown as it is. */
  readonly label?: string;
  /**
   * An i18n key, for the case where there is no name yet — a new entry.
   *
   * Kept as a key rather than translated at the source so the breadcrumb still
   * follows a language change while the form is open.
   */
  readonly labelKey?: string;
  readonly close: () => void;
  /**
   * The form needs the whole screen rather than the sheet's usual width.
   *
   * Reported here rather than measured by the host: only the section knows
   * what it is about to render, and the Blockly canvas is unusable in 680px.
   */
  readonly wide?: boolean;
}

@Injectable({ providedIn: 'root' })
export class OrgUnitSettingsNavigation {
  readonly #lastSection = signal<OrgUnitSettingsSectionId | null>(null);
  readonly #openItem = signal<OpenSettingsItem | null>(null);

  readonly lastSection = this.#lastSection.asReadonly();
  readonly openItem = this.#openItem.asReadonly();

  remember(section: OrgUnitSettingsSectionId | null): void {
    this.#lastSection.set(section);
    // Leaving a section takes its form with it, and the section has to be told:
    // it lives on through content projection even while its slot is not
    // rendered, so an unclosed form is still open when the section is next
    // reached — the entry would reappear without anyone asking for it.
    this.leaveItem();
  }

  /** A section reports that it is showing one entry's form. */
  enterItem(item: OpenSettingsItem): void {
    this.#openItem.set(item);
  }

  /** A section reports that it closed the form itself — saved or cancelled. */
  clearItem(): void {
    this.#openItem.set(null);
  }

  /**
   * Forget everything — the preview was closed.
   *
   * Both halves, not just the open form: the remembered section is part of the
   * breadcrumb too, and resetting one of the two leaves the trail half-stale.
   */
  reset(): void {
    this.leaveItem();
    this.#lastSection.set(null);
  }

  /** The breadcrumb sends the open section back to its list. */
  leaveItem(): void {
    const item = this.#openItem();
    this.#openItem.set(null);
    item?.close();
  }
}
